import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Practitioner } from '@/types/practitioner';
import { getFullName } from '@/utils/name-helpers';
import { useSEO } from '@/hooks/useSEO';
import { useStructuredData } from '@/components/seo/StructuredData';
import { APP_CONFIG } from '@/config/app-config';

interface PractitionerSEOProps {
  practitioner: Practitioner;
}

export const PractitionerSEO: React.FC<PractitionerSEOProps> = ({ practitioner }) => {
  const { t, i18n } = useTranslation('practitioners');
  const { updateSEO } = useSEO();
  const { getPersonSchema, getProfessionalServiceSchema } = useStructuredData();

  useEffect(() => {
    const practitionerName = getFullName(practitioner);
    const services = practitioner.services || [];
    const minPrice = services.length > 0 ? Math.min(...services.map(s => s.price)) : null;
    const maxPrice = services.length > 0 ? Math.max(...services.map(s => s.price)) : null;
    const priceRange = minPrice && maxPrice ? `${minPrice}-${maxPrice} ${services[0]?.currency || 'USD'}` : null;

    // Create comprehensive meta description
    const metaDescription = practitioner.bio 
      ? `${practitioner.bio.substring(0, 120)}...`
      : t('practitioner.defaultDescription', { 
          name: practitionerName, 
          title: practitioner.professional_title || t('practitioner.defaultTitle')
        });

    // Generate keywords
    const keywords = [
      practitionerName,
      practitioner.professional_title,
      t('practitioner.keywords.therapy'),
      t('practitioner.keywords.healing'),
      t('practitioner.keywords.wellness'),
      ...services.map(s => s.service_name),
      ...(practitioner.session_formats_offered || []).map(format => 
        format === 'online' ? t('practitioner.keywords.online') : t('practitioner.keywords.inPerson')
      ),
    ].filter(Boolean);

    const seoData = {
      title: `${practitionerName} - ${practitioner.professional_title || t('practitioner.defaultTitle')}`,
      description: metaDescription,
      keywords: keywords.join(', '),
      canonical: `${APP_CONFIG.PRODUCTION_DOMAIN}/book-an-appointment/${practitioner.slug}`,
      openGraph: {
        title: `${practitionerName} - ${practitioner.professional_title || t('practitioner.defaultTitle')}`,
        description: metaDescription,
        image: practitioner.avatar_url || '/images/default-practitioner.jpg',
        url: `${APP_CONFIG.PRODUCTION_DOMAIN}/book-an-appointment/${practitioner.slug}`,
        type: 'profile',
        locale: i18n.language,
        siteName: t('site.name'),
        profile: {
          first_name: practitioner.first_name,
          last_name: practitioner.last_name,
          gender: practitioner.gender,
        }
      },
      twitter: {
        card: 'summary_large_image',
        title: `${practitionerName} - ${practitioner.professional_title || t('practitioner.defaultTitle')}`,
        description: metaDescription,
        image: practitioner.avatar_url || '/images/default-practitioner.jpg',
        creator: '@yoursite',
      },
      structuredData: [
        getPersonSchema(practitioner),
        getProfessionalServiceSchema(),
        // Enhanced HealthcareService schema
        {
          "@context": "https://schema.org",
          "@type": "HealthcareService",
          "name": `${practitionerName} - ${practitioner.professional_title}`,
          "description": metaDescription,
          "provider": {
            "@type": "Person",
            "name": practitionerName,
            "jobTitle": practitioner.professional_title,
            "image": practitioner.avatar_url,
            "url": `${APP_CONFIG.PRODUCTION_DOMAIN}/book-an-appointment/${practitioner.slug}`,
            "knowsAbout": services.map(s => s.service_name),
            "hasCredential": (practitioner as any).certifications?.map((cert: any) => ({
              "@type": "EducationalOccupationalCredential",
              "name": cert.certificate_name,
              "credentialCategory": cert.certification_type,
              "recognizedBy": cert.issuing_organization,
              "dateCreated": cert.issue_date,
              "expires": cert.expiry_date,
            })) || [],
            "workLocation": practitioner.in_person_address ? {
              "@type": "Place",
              "address": practitioner.in_person_address,
            } : undefined,
            "availableLanguage": practitioner.languages_spoken || [i18n.language],
          },
          "serviceType": practitioner.professional_title,
          "availableLanguage": practitioner.languages_spoken || [i18n.language],
          "areaServed": practitioner.country || "Global",
          "serviceArea": practitioner.country ? {
            "@type": "AdministrativeArea",
            "name": practitioner.country,
          } : {
            "@type": "Place",
            "name": "Global",
          },
          "offers": services.map(service => ({
            "@type": "Offer",
            "name": service.service_name,
            "description": service.description,
            "price": service.price,
            "priceCurrency": service.currency,
            "availability": "https://schema.org/InStock",
            "url": `${APP_CONFIG.PRODUCTION_DOMAIN}/book-an-appointment/${practitioner.slug}#service-${service.id}`,
            "validFrom": new Date().toISOString(),
            "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
          })),
          "aggregateRating": practitioner.average_rating && practitioner.total_reviews > 0 ? {
            "@type": "AggregateRating",
            "ratingValue": practitioner.average_rating,
            "reviewCount": practitioner.total_reviews,
            "bestRating": 5,
            "worstRating": 1,
          } : undefined,
          "review": (practitioner as any).reviews?.slice(0, 5).map((review: any) => ({
            "@type": "Review",
            "author": {
              "@type": "Person",
              "name": review.anonymous ? "Anonymous" : `${review.client?.first_name || ''} ${review.client?.last_name || ''}`.trim() || "Anonymous",
            },
            "reviewRating": {
              "@type": "Rating",
              "ratingValue": review.rating,
              "bestRating": 5,
              "worstRating": 1,
            },
            "reviewBody": review.comment,
            "datePublished": review.created_at,
            "publisher": {
              "@type": "Organization",
              "name": "Resonow",
            },
          })) || [],
        },
        // LocalBusiness schema for practitioners with physical locations
        ...(practitioner.in_person_address ? [{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": `${practitionerName} - ${practitioner.professional_title}`,
          "description": metaDescription,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": practitioner.in_person_address,
            "addressCountry": practitioner.country,
          },
          "geo": practitioner.latitude && practitioner.longitude ? {
            "@type": "GeoCoordinates",
            "latitude": practitioner.latitude,
            "longitude": practitioner.longitude,
          } : undefined,
          "telephone": (practitioner as any).phone_number,
          "email": `info@resonow.org`,
          "url": `${APP_CONFIG.PRODUCTION_DOMAIN}/book-an-appointment/${practitioner.slug}`,
          "image": practitioner.avatar_url,
          "priceRange": priceRange,
          "aggregateRating": practitioner.average_rating && practitioner.total_reviews > 0 ? {
            "@type": "AggregateRating",
            "ratingValue": practitioner.average_rating,
            "reviewCount": practitioner.total_reviews,
            "bestRating": 5,
            "worstRating": 1,
          } : undefined,
          "openingHours": (practitioner as any).availability_rules?.weekdays?.map((day: any) => 
            `${day.day} ${day.start_time}-${day.end_time}`
          ) || [],
        }] : []),
        // Professional Person schema with enhanced details
        {
          "@context": "https://schema.org",
          "@type": "Person",
          "name": practitionerName,
          "jobTitle": practitioner.professional_title,
          "description": practitioner.bio || metaDescription,
          "image": practitioner.avatar_url,
          "url": `${APP_CONFIG.PRODUCTION_DOMAIN}/book-an-appointment/${practitioner.slug}`,
          "worksFor": {
            "@type": "Organization",
            "name": "Resonow",
            "url": APP_CONFIG.PRODUCTION_DOMAIN,
          },
          "knowsAbout": services.map(s => s.service_name),
          "hasCredential": (practitioner as any).certifications?.map((cert: any) => ({
            "@type": "EducationalOccupationalCredential",
            "name": cert.certificate_name,
            "credentialCategory": cert.certification_type,
            "recognizedBy": cert.issuing_organization,
          })) || [],
          "availableLanguage": practitioner.languages_spoken || [i18n.language],
          "alumniOf": practitioner.education ? {
            "@type": "EducationalOrganization",
            "name": practitioner.education,
          } : undefined,
          "workLocation": practitioner.in_person_address ? {
            "@type": "Place",
            "address": practitioner.in_person_address,
          } : undefined,
          "gender": practitioner.gender,
          "nationality": practitioner.country,
        },
      ]
    };

    updateSEO(seoData);
  }, [practitioner, t, i18n.language, updateSEO, getPersonSchema, getProfessionalServiceSchema]);

  return null;
};